"use client";

import { motion, type Variants } from "framer-motion";
import { type ReactNode } from "react";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const staggerFast: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

/**
 * On-mount animation: plays immediately (initial + animate).
 * Use in hero sections that should animate on page load.
 */
export function MotionEntrance({
  children,
  className,
  staggerDelay,
}: {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={
        staggerDelay != null
          ? { hidden: {}, visible: { transition: { staggerChildren: staggerDelay } } }
          : stagger
      }
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Viewport-triggered animation: plays when scrolled into view.
 */
export function MotionView({
  children,
  className,
  staggerDelay,
}: {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={
        staggerDelay != null
          ? { hidden: {}, visible: { transition: { staggerChildren: staggerDelay } } }
          : stagger
      }
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Fade-up child item (used inside MotionEntrance or MotionView).
 */
export function MotionFadeUp({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div variants={fadeInUp} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * Scale-in child item (used inside MotionEntrance or MotionView).
 */
export function MotionScaleIn({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div variants={scaleIn} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * Stagger wrapper with fast timing (0.08s between children).
 */
export function MotionViewFast({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerFast}
      className={className}
    >
      {children}
    </motion.div>
  );
}
